import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import makeApi, { URLS } from "Utils/apiURL";
import { phoneNumberStore } from "Components/ContactCard/phoneNumberStore";
import { toasterState } from "Components/common/toaster";
import { customListState } from "Pages/Dashboard/dataSearchV2/CustomList/state";

const initials = (name) => {
  if (!name) return "?";
  const parts = String(name).trim().split(/\s+/);
  return ((parts[0]?.[0] || "") + (parts[parts.length - 1]?.[0] || "")).toUpperCase();
};

const titleCase = (s) =>
  String(s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

const REL_META = {
  prospect:       { label: "This Contact",   color: "#5b21b6", hint: "The prospect — primary target." },
  likely_manager: { label: "Likely Manager", color: "#7c3aed", hint: "One tier above — exec-sponsor angle." },
  peer:           { label: "Peer",           color: "#2563eb", hint: "Same level + dept. Multi-thread the deal." }
};

const ensureHttps = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://${url}`;
};

// material-icons has no LinkedIn brand mark, so inline the logo glyph.
const LinkedInGlyph = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="currentColor" aria-hidden="true">
    <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14zM8.34 18.34V9.93H5.55v8.41h2.79zM6.95 8.69a1.62 1.62 0 1 0 0-3.24 1.62 1.62 0 0 0 0 3.24zm11.39 9.65v-4.61c0-2.46-1.31-3.6-3.07-3.6-1.17 0-1.95.64-2.27 1.25h-.04v-1.05H10.1v8.41h2.78v-4.16c0-1.1.21-2.16 1.57-2.16 1.34 0 1.36 1.25 1.36 2.23v4.09h2.92z"/>
  </svg>
);

// Build a /list-builder deep link using the LLM-framed filters, scoped to
// this account's domain. Same filters that surfaced these intros.
const buildListBuilderUrl = (domain, filters, overrideLevel) => {
  if (!domain) return null;
  const qs = new URLSearchParams();
  qs.set("company", domain);
  const lvl = overrideLevel ? [overrideLevel] : (filters?.level || []);
  if (lvl.length) qs.set("level", lvl.join(","));
  const depts = filters?.department || [];
  if (depts.length) qs.set("department", depts.join(","));
  const titles = filters?.title || [];
  if (titles.length) qs.set("title", titles.join(","));
  return `/list-builder?${qs.toString()}`;
};

// Map an AI-Ark warm-intro contact to the /cm/contacts/ directory payload row.
const toDirectoryContact = (contact) => {
  const li = ensureHttps(contact.linkedIn || contact.linkedinId) || "";
  return {
    firstName: contact.firstName || "",
    lastName: contact.lastName || "",
    company: contact.company || contact.companyName || "",
    domain: contact.companyDomain || "",
    jobTitle: contact.title || contact.subTitle || "",
    industry: contact.companyIndustry || "",
    linkedinId: li.replace(/^https?:\/\//, ""),
    email: contact.email || null,
    city: contact.city || "",
    state: contact.state || "",
    country: contact.country || "",
    liCompanyId: contact.companyLinkedIn || ""
  };
};

// ─────────────────────────────────────────────────────────────────
// ListPicker — shared add-to-list popover. Used by the per-contact
// card ("Add to list") and the section header ("Add all to a list").
// `onPick(list)` does the actual API call and returns a promise; the
// picker owns open/close + busy + the create-new-list flow.
// ─────────────────────────────────────────────────────────────────
const ListPicker = ({ trigger, onPick, onToggle }) => {
  const [open, setOpen] = useState(false);
  const [lists, setLists] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");

  const setOpenState = (v) => {
    setOpen(v);
    if (onToggle) onToggle(v);
  };

  const toggle = async () => {
    const next = !open;
    setOpenState(next);
    if (next && lists === null) {
      setLoading(true);
      await customListState.getContactList();
      setLists(customListState.totalContactList || []);
      setLoading(false);
    }
  };

  const choose = async (list) => {
    if (!list || busy) return;
    setBusy(true);
    await onPick(list);
    setBusy(false);
    setOpenState(false);
  };

  const createAndChoose = async () => {
    const nm = newName.trim();
    if (!nm || busy) return;
    setBusy(true);
    const created = await customListState.createContactList(nm);
    if (created && (created.id || created._id)) {
      setNewName("");
      await onPick(created);
      setBusy(false);
      setOpenState(false);
    } else {
      setBusy(false);
      toasterState.setToastMsg("Couldn't create list.", "failure");
    }
  };

  return (
    <div className="drIntro__listWrap">
      <button
        type="button"
        className={trigger.className}
        onClick={toggle}
        title="Add to a list"
      >
        {trigger.icon && (
          <i className="material-icons-outlined drIntro__ctaIcon">{trigger.icon}</i>
        )}
        {trigger.label}
      </button>
      {open && (
        <div className="drIntro__listPicker">
          <div className="drIntro__listPickerHead">Add to a list</div>
          {loading ? (
            <div className="drIntro__listPickerEmpty">Loading lists…</div>
          ) : (lists && lists.length > 0) ? (
            <div className="drIntro__listPickerScroll">
              {lists.map((l) => (
                <button
                  key={l.id || l._id || l.name}
                  type="button"
                  className="drIntro__listPickerItem"
                  onClick={() => choose(l)}
                  disabled={busy}
                >
                  <i className="material-icons-outlined drIntro__listPickerItemIcon">format_list_bulleted</i>
                  <span>{l.name}</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="drIntro__listPickerEmpty">No lists yet — create one below.</div>
          )}
          <div className="drIntro__listPickerNew">
            <input
              type="text"
              className="drIntro__listPickerInput"
              placeholder="New list name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") createAndChoose(); }}
              disabled={busy}
            />
            <button
              type="button"
              className="drIntro__listPickerCreate"
              onClick={createAndChoose}
              disabled={busy || !newName.trim()}
            >
              {busy ? "…" : "Create"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
// Shared ContactCard — the prospect (CM contact, contactId set) and
// the AI-Ark warm-intro contacts (no contactId, identified by
// LinkedIn URL). Warm-intro cards get the List-Builder-parity reveal
// (View email / View phone) plus Add-to-list and a "why" line.
// ─────────────────────────────────────────────────────────────────
const ContactCard = ({ contact, relationship, contactId, listBuilderHref }) => {
  const history = useHistory();
  const rel = REL_META[relationship] || REL_META.peer;
  const name = contact.name || `${contact.firstName || ""} ${contact.lastName || ""}`.trim();
  const title = contact.title || contact.subTitle || "";
  const location = contact.location || "";
  const company = contact.company || contact.companyName || "";
  const linkedinHref = ensureHttps(contact.linkedIn || contact.linkedinId);
  const twitterHref = ensureHttps(contact.twitter);
  const githubHref = ensureHttps(contact.github);
  const personalSite = ensureHttps(contact.personalSite);
  // The prospect carries a CM contactId; AI-Ark warm-intro contacts don't.
  const isWarmIntro = !contactId;

  // One-line "why this person is a target" — derived, no LLM call.
  const whyLine = (() => {
    if (relationship === "likely_manager") {
      return "One level up — bring in as your executive sponsor.";
    }
    if (relationship === "peer") {
      const dept = contact.matched_department;
      return dept
        ? `Peer in ${titleCase(dept)} — multi-thread the deal.`
        : "Same level as the prospect — multi-thread the deal.";
    }
    return null; // prospect — no why line
  })();

  const [email, setEmail] = useState(contact.email || "");
  const [phone, setPhone] = useState(contact.phone || contact.mobilePhone || "");
  const [emailLoading, setEmailLoading] = useState(false);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [emailMissing, setEmailMissing] = useState(false);
  const [phoneMissing, setPhoneMissing] = useState(false);
  const [listOpen, setListOpen] = useState(false);

  // ── Prospect reveal — existing CM contact-scoped path (unchanged). ──
  const findEmailProspect = () => {
    setEmailLoading(true);
    makeApi({ url: `${URLS.findEmailFromLinkedIn}/${contactId}`, method: "POST" })
      .then(res => {
        const found = res?.data?.email || res?.data?.contact?.email;
        if (found) setEmail(found); else setEmailMissing(true);
        setEmailLoading(false);
      }).catch(() => setEmailLoading(false));
  };
  const findPhoneProspect = () => {
    setPhoneLoading(true);
    phoneNumberStore.purchasePhoneNumber(contactId).then(res => {
      const found = res?.data?.phoneNumber || res?.data?.mobilePhone || res?.data?.phone;
      if (found) setPhone(found); else setPhoneMissing(true);
      setPhoneLoading(false);
    }).catch(() => setPhoneLoading(false));
  };

  // ── Warm-intro reveal — List Builder parity: POST /cm/tp/contact keyed
  //    by the LinkedIn profile URL. Charges credits, returns contact.email
  //    / contact.mobilePhone. Mirrors dataSearchV2 purchaseSingleContact*. ──
  const revealEmail = () => {
    if (!linkedinHref || emailLoading) return;
    setEmailLoading(true);
    setEmailMissing(false);
    makeApi({
      url: URLS.findEmailFromLinkedIn,
      method: "POST",
      data: {
        type: "EMAIL",
        linkedInProfileUrl: linkedinHref,
        fullName: name,
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        city: contact.city || "",
        state: contact.state || "",
        country: contact.country || "",
        companyLinkedIn: contact.companyLinkedIn || ""
      }
    }).then(res => {
      const data = res?.data ?? res?.response?.data ?? {};
      const code = String(data.responseCode ?? data.statusCode ?? "");
      const found = data?.contact?.email || "";
      if (found) {
        setEmail(found);
        toasterState.setToastMsg(
          data.creditsUsed
            ? `Email revealed — ${data.creditsUsed} credit used.`
            : "Email revealed.",
          "success"
        );
      } else if (code === "204") {
        toasterState.setToastMsg("Not enough credits to reveal email.", "failure");
      } else {
        setEmailMissing(true);
        toasterState.setToastMsg("No email found for this contact.", "failure");
      }
      setEmailLoading(false);
    }).catch(() => {
      setEmailLoading(false);
      toasterState.setToastMsg("Couldn't reveal email — try again.", "failure");
    });
  };
  const revealPhone = () => {
    if (!linkedinHref || phoneLoading) return;
    setPhoneLoading(true);
    setPhoneMissing(false);
    makeApi({
      url: URLS.findEmailFromLinkedIn,
      method: "POST",
      data: {
        type: "PHONE",
        linkedInProfileUrl: linkedinHref,
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        city: contact.city || "",
        state: contact.state || "",
        country: contact.country || "",
        companyLinkedIn: contact.companyLinkedIn || ""
      }
    }).then(res => {
      const data = res?.data ?? res?.response?.data ?? {};
      const code = String(data.responseCode ?? data.statusCode ?? "");
      const found = data?.contact?.mobilePhone || "";
      if (found) {
        setPhone(found);
        if (data?.contact?.email && !email) setEmail(data.contact.email);
        toasterState.setToastMsg(
          data.creditsUsed
            ? `Phone revealed — ${data.creditsUsed} credit used.`
            : "Phone revealed.",
          "success"
        );
      } else if (code === "204") {
        toasterState.setToastMsg("Not enough credits to reveal phone.", "failure");
      } else {
        setPhoneMissing(true);
        toasterState.setToastMsg("No phone found for this contact.", "failure");
      }
      setPhoneLoading(false);
    }).catch(() => {
      setPhoneLoading(false);
      toasterState.setToastMsg("Couldn't reveal phone — try again.", "failure");
    });
  };

  const onRevealEmail = isWarmIntro ? revealEmail : findEmailProspect;
  const onRevealPhone = isWarmIntro ? revealPhone : findPhoneProspect;
  const canReveal = isWarmIntro ? !!linkedinHref : !!contactId;

  // Add this single contact to a list (List Builder parity, POST /cm/contacts/).
  const addToList = (list) =>
    makeApi({
      url: URLS.addContactsToDirectory,
      method: "POST",
      data: {
        contacts: [{ ...toDirectoryContact(contact), email: email || null }],
        contactTags: [],
        listId: list.id || list._id || null,
        useSnCredits: true
      }
    }).then(res => {
      const data = res?.data ?? {};
      const code = String(data.responseCode ?? data.statusCode ?? "");
      if (["200", "201", "202"].includes(code)) {
        toasterState.setToastMsg(`Added ${name} to "${list.name}".`, "success");
      } else if (code === "402") {
        toasterState.setToastMsg("Upgrade required to add more contacts.", "failure");
      } else {
        toasterState.setToastMsg("Couldn't add to list — try again.", "failure");
      }
    }).catch(() => {
      toasterState.setToastMsg("Couldn't add to list.", "failure");
    });

  // "More like this" → in-app navigation to List Builder (no full reload).
  const goToListBuilder = (e) => {
    e.preventDefault();
    if (listBuilderHref) history.push(listBuilderHref);
  };

  // AI-Ark availability hints — shown before the rep spends credits.
  const emailAvailable = !!contact.hasEmail || contact.emailStatus === "verified";
  const phoneAvailable = !!contact.hasMobile || !!contact.hasDirectPhone || !!contact.hasPhone;

  return (
    <div className={`drIntro__card ${relationship === "prospect" ? "drIntro__card--prospect" : ""}${listOpen ? " drIntro__card--listOpen" : ""}`}>
      {/* Header — avatar + identity */}
      <div className="drIntro__cardTop">
        {contact.profilePic ? (
          <img src={contact.profilePic} alt="" className="drIntro__avatarImg" />
        ) : (
          <div className="drIntro__avatar" style={{ background: rel.color }}>
            {initials(name)}
          </div>
        )}
        <div className="drIntro__cardMeta">
          <div className="drIntro__name">{name || "—"}</div>
          {title && <div className="drIntro__title">{title}</div>}
          {(location || company) && (
            <div className="drIntro__sub">
              {company && <span>{company}</span>}
              {company && location && <span className="drIntro__subDot">·</span>}
              {location && <span>{location}</span>}
            </div>
          )}
        </div>
        {/* Top-right — relationship badge + LinkedIn profile shortcut */}
        <div className="drIntro__cardTopRight">
          <span
            className="drIntro__relBadge"
            style={{ color: rel.color, borderColor: `${rel.color}33`, background: `${rel.color}0d` }}
            title={rel.hint}
          >
            {rel.label}
          </span>
          {linkedinHref && (
            <a
              href={linkedinHref}
              target="_blank"
              rel="noopener noreferrer"
              className="drIntro__liIcon"
              title="Open LinkedIn profile"
              aria-label="Open LinkedIn profile"
            >
              <LinkedInGlyph />
            </a>
          )}
        </div>
      </div>

      {/* Why this person is a target (warm-intro cards only) */}
      {whyLine && (
        <div className="drIntro__why">
          <i className="material-icons-outlined drIntro__whyIcon">my_location</i>
          <span>{whyLine}</span>
        </div>
      )}

      {/* Reach-out CTAs — reveal + add-to-list */}
      <div className="drIntro__ctaRow">
        {/* Email */}
        {email ? (
          <a className="drIntro__cta drIntro__cta--primary" href={`mailto:${email}`} title={email}>
            <i className="material-icons-outlined drIntro__ctaIcon">mail</i>
            Email
          </a>
        ) : canReveal ? (
          <button
            type="button"
            className="drIntro__cta drIntro__cta--ghost"
            onClick={onRevealEmail}
            disabled={emailLoading || emailMissing}
            title={emailMissing
              ? "No email found"
              : (emailAvailable ? "Email available — uses credits" : "Reveal email — uses credits")}
          >
            <i className="material-icons-outlined drIntro__ctaIcon">mail</i>
            {emailLoading ? "Revealing…" : emailMissing ? "No email" : "View email"}
            {emailAvailable && !emailLoading && !emailMissing && <span className="drIntro__ctaDot" />}
          </button>
        ) : null}

        {/* Phone */}
        {phone ? (
          <a className="drIntro__cta drIntro__cta--secondary" href={`tel:${phone}`} title={phone}>
            <i className="material-icons-outlined drIntro__ctaIcon">call</i>
            Call
          </a>
        ) : canReveal ? (
          <button
            type="button"
            className="drIntro__cta drIntro__cta--ghost"
            onClick={onRevealPhone}
            disabled={phoneLoading || phoneMissing}
            title={phoneMissing
              ? "No phone found"
              : (phoneAvailable ? "Phone available — uses credits" : "Reveal phone — uses credits")}
          >
            <i className="material-icons-outlined drIntro__ctaIcon">call</i>
            {phoneLoading ? "Revealing…" : phoneMissing ? "No phone" : "View phone"}
            {phoneAvailable && !phoneLoading && !phoneMissing && <span className="drIntro__ctaDot" />}
          </button>
        ) : null}

        {/* Add to list — warm-intro contacts only (prospect is already a contact) */}
        {isWarmIntro && (
          <ListPicker
            trigger={{
              label: "Add to list",
              icon: "playlist_add",
              className: "drIntro__cta drIntro__cta--secondary"
            }}
            onPick={addToList}
            onToggle={setListOpen}
          />
        )}
      </div>

      {/* Footer — secondary socials + find-more */}
      <div className="drIntro__cardFooter">
        <div className="drIntro__cardActions">
          {twitterHref && (
            <a href={twitterHref} target="_blank" rel="noopener noreferrer"
               className="drIntro__actionLink" title="View on X/Twitter">
              <i className="material-icons-outlined drIntro__actionIcon">alternate_email</i>
            </a>
          )}
          {githubHref && (
            <a href={githubHref} target="_blank" rel="noopener noreferrer"
               className="drIntro__actionLink" title="GitHub">
              <i className="material-icons-outlined drIntro__actionIcon">code</i>
            </a>
          )}
          {personalSite && (
            <a href={personalSite} target="_blank" rel="noopener noreferrer"
               className="drIntro__actionLink" title="Personal site">
              <i className="material-icons-outlined drIntro__actionIcon">language</i>
            </a>
          )}
        </div>
        {listBuilderHref && (
          <a
            href={listBuilderHref}
            onClick={goToListBuilder}
            className="drIntro__moreLink"
            title="Find more contacts like this in List Builder"
          >
            <i className="material-icons-outlined drIntro__moreLinkIcon">search</i>
            <span className="drIntro__moreLinkText">More like this</span>
          </a>
        )}
      </div>
    </div>
  );
};

const WarmIntros = ({ intros, contactData, prospect }) => {
  const [open, setOpen] = useState(true);

  // Backwards-compat: older payloads were a flat array. New shape is
  // { filters, rationale, contacts, domain }.
  const isLegacyArray = Array.isArray(intros);
  const aiarkContacts = isLegacyArray
    ? intros
    : (Array.isArray(intros?.contacts) ? intros.contacts : []);
  const filters = isLegacyArray ? {} : (intros?.filters || {});
  const rationale = isLegacyArray ? "" : (intros?.rationale || "");
  const domain = isLegacyArray ? null : (intros?.domain || null);

  // Build the prospect's unified contact card data from research + contactData.
  const prospectCard = (() => {
    if (!contactData && !prospect) return null;
    return {
      firstName: prospect?.firstName || contactData?.firstName || "",
      lastName: prospect?.lastName || contactData?.lastName || "",
      name: `${prospect?.firstName || contactData?.firstName || ""} ${prospect?.lastName || contactData?.lastName || ""}`.trim(),
      title: prospect?.subTitle || prospect?.title || contactData?.jobTitle || "",
      company: contactData?.company || prospect?.company_data?.company_data?.name || "",
      location: prospect?.location || [contactData?.city, contactData?.state, contactData?.country].filter(Boolean).join(", "),
      linkedIn: contactData?.linkedinId || prospect?.linkedinId,
      email: contactData?.email || "",
      phone: contactData?.phone || contactData?.mobilePhone || "",
      profilePic: prospect?.profilePicture || prospect?.profile_picture
    };
  })();

  const contactId = contactData?.contactId || contactData?.id;
  const hasProspect = !!prospectCard && (prospectCard.name || prospectCard.title);

  if (!hasProspect && aiarkContacts.length === 0) return null;

  const managers = aiarkContacts.filter(c => c.relationship_to_prospect === "likely_manager");
  const peers = aiarkContacts.filter(c => c.relationship_to_prospect === "peer");
  const totalShown = (hasProspect ? 1 : 0) + aiarkContacts.length;
  const sectionListBuilderHref = buildListBuilderUrl(domain, filters);

  // Add every warm-intro contact (managers + peers) to a list in one call.
  const addAllToList = (list) =>
    makeApi({
      url: URLS.addContactsToDirectory,
      method: "POST",
      data: {
        contacts: aiarkContacts.map(toDirectoryContact),
        contactTags: [],
        listId: list.id || list._id || null,
        useSnCredits: true
      }
    }).then(res => {
      const data = res?.data ?? {};
      const code = String(data.responseCode ?? data.statusCode ?? "");
      if (["200", "201", "202"].includes(code)) {
        toasterState.setToastMsg(
          `Added ${aiarkContacts.length} contacts to "${list.name}".`,
          "success"
        );
      } else if (code === "402") {
        toasterState.setToastMsg("Upgrade required to add more contacts.", "failure");
      } else {
        toasterState.setToastMsg("Couldn't add contacts to list — try again.", "failure");
      }
    }).catch(() => {
      toasterState.setToastMsg("Couldn't add contacts to list.", "failure");
    });

  return (
    <div className="drSection drIntro">
      <button
        type="button"
        className="drIntro__header"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
      >
        <i className="material-icons-outlined drIntro__chevron">
          {open ? "expand_more" : "chevron_right"}
        </i>
        <span className="drSectionIcon">🤝</span>
        <h3 className="drIntro__title">Contacts at this account</h3>
        <span className="drIntro__count">{totalShown}</span>
        {sectionListBuilderHref && (
          <a
            href={sectionListBuilderHref}
            target="_blank"
            rel="noopener noreferrer"
            className="drIntro__headerLink"
            onClick={e => e.stopPropagation()}
          >
            View all in List Builder ↗
          </a>
        )}
      </button>

      {open && (
        <>
          {rationale && (
            <div className="drIntro__rationale">
              <div className="drIntro__rationaleRail">
                <i className="material-icons-outlined drIntro__rationaleIcon">auto_awesome</i>
                <span className="drIntro__rationaleLabel">Multi-thread strategy</span>
              </div>
              <p className="drIntro__rationaleText">{rationale}</p>
            </div>
          )}

          {/* Section action — drop every warm-intro contact into a list at once */}
          {aiarkContacts.length > 0 && (
            <div className="drIntro__sectionActions">
              <ListPicker
                trigger={{
                  label: `Add all ${aiarkContacts.length} to a list`,
                  icon: "playlist_add",
                  className: "drIntro__cta drIntro__cta--secondary"
                }}
                onPick={addAllToList}
              />
            </div>
          )}

          {hasProspect && (
            <div className="drIntro__group">
              <div className="drIntro__groupLabel">Prospect</div>
              <div className="drIntro__cards">
                <ContactCard
                  contact={prospectCard}
                  relationship="prospect"
                  contactId={contactId}
                />
              </div>
            </div>
          )}

          {managers.length > 0 && (
            <div className="drIntro__group">
              <div className="drIntro__groupLabel">Likely Manager</div>
              <div className="drIntro__cards">
                {managers.map((c, i) => (
                  <ContactCard
                    key={`m${i}`}
                    contact={c}
                    relationship="likely_manager"
                    listBuilderHref={buildListBuilderUrl(domain, filters, c.matched_level)}
                  />
                ))}
              </div>
            </div>
          )}

          {peers.length > 0 && (
            <div className="drIntro__group">
              <div className="drIntro__groupLabel">Peers · Multi-thread Targets</div>
              <div className="drIntro__cards">
                {peers.map((c, i) => (
                  <ContactCard
                    key={`p${i}`}
                    contact={c}
                    relationship="peer"
                    listBuilderHref={buildListBuilderUrl(domain, filters, c.matched_level)}
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default WarmIntros;
